import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useAssignFoundingBulk } from "./use-founding-mutations";
import { useFoundingRates } from "./use-founding-rates";
import {
  FOUNDING_INTRO_PLACEHOLDER,
  FOUNDING_ONGOING_PLACEHOLDER,
  FOUNDING_PROGRAMS,
  MAX_DISCOUNT_PERCENT,
  MIN_DISCOUNT_PERCENT,
} from "./constants";
import type { AssignFoundingBulkBody } from "./founding-api";
import type { BulkFoundingResult, FoundingProgram } from "./types";

/**
 * The rate structure for a PORTFOLIO_CUSTOM enrollment (ADR-0010 §9, choice B2):
 * either a two-phase intro→ongoing schedule (steps down like a host after Year
 * 1) or a single flat rate that never changes. FOUNDING_HOST is always
 * two-phase, so the toggle only shows for PORTFOLIO_CUSTOM.
 */
type RateStructure = "two-phase" | "flat";

interface AssignFoundingDialogProps {
  readonly propertyIds: readonly string[];
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onDone: () => void;
}

/** True when `value` is an integer discount percent in the accepted range. */
function isValidPercent(value: string): boolean {
  const n = Number(value);
  return (
    value.trim() !== "" &&
    Number.isInteger(n) &&
    n >= MIN_DISCOUNT_PERCENT &&
    n <= MAX_DISCOUNT_PERCENT
  );
}

export function AssignFoundingDialog({
  propertyIds,
  open,
  onOpenChange,
  onDone,
}: AssignFoundingDialogProps) {
  const { t } = useTranslation();
  const { data: rates } = useFoundingRates(open);
  const [program, setProgram] = useState<FoundingProgram>("FOUNDING_HOST");
  const [structure, setStructure] = useState<RateStructure>("two-phase");
  const [intro, setIntro] = useState("");
  const [ongoing, setOngoing] = useState("");
  const [flat, setFlat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkFoundingResult | null>(null);
  const assign = useAssignFoundingBulk();

  // Reset the whole form every time the dialog (re)opens. Intro/ongoing pre-fill
  // from the fetched defaults; if the rates query hasn't resolved yet they stay
  // empty and the effect below hydrates them once the data arrives.
  useEffect(() => {
    if (open) {
      setProgram("FOUNDING_HOST");
      setStructure("two-phase");
      setIntro(rates ? String(rates.introPercent) : "");
      setOngoing(rates ? String(rates.ongoingPercent) : "");
      setFlat("");
      setError(null);
      setResult(null);
    }
    // Reset from the current state on open only; `rates` is handled separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Hydrate the intro/ongoing inputs when the defaults resolve after the dialog
  // has already opened. Only fill still-empty fields so a mid-edit admin value
  // is never clobbered.
  useEffect(() => {
    if (open && rates) {
      setIntro((prev) => (prev === "" ? String(rates.introPercent) : prev));
      setOngoing((prev) => (prev === "" ? String(rates.ongoingPercent) : prev));
    }
  }, [open, rates]);

  const isCustom = program === "PORTFOLIO_CUSTOM";
  const isFlat = isCustom && structure === "flat";
  // FOUNDING_HOST and two-phase PORTFOLIO_CUSTOM both use intro + ongoing.
  const showTwoPhase = !isFlat;

  const twoPhaseValid = isValidPercent(intro) && isValidPercent(ongoing);
  const flatValid = isValidPercent(flat);
  const fieldsValid = isFlat ? flatValid : twoPhaseValid;
  const canSubmit = propertyIds.length > 0 && fieldsValid && !assign.isPending;

  const handleClose = (next: boolean) => {
    if (!next && assign.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    setError(null);
    // Send ONLY the fields the chosen program/structure needs — never flat
    // together with intro/ongoing.
    const body: AssignFoundingBulkBody = isFlat
      ? {
          propertyIds: [...propertyIds],
          program,
          flatPercent: Number(flat),
        }
      : {
          propertyIds: [...propertyIds],
          program,
          introPercent: Number(intro),
          ongoingPercent: Number(ongoing),
        };

    assign.mutate(body, {
      onSuccess: (res) => {
        setResult(res);
        onDone();
      },
      onError: (err) => {
        setError(
          resolveMutationErrorMessage(err, t, {
            forbiddenKey: "founding.errors.forbidden",
            serverPrefixKey: "founding.errors.serverPrefix",
            genericKey: "founding.errors.generic",
          }),
        );
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="assign-founding-dialog">
        <DialogHeader>
          <DialogTitle>{t("founding.assign.title")}</DialogTitle>
          <DialogDescription>
            {t("founding.assign.description", { count: propertyIds.length })}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-2 py-4" data-testid="assign-founding-result">
            <p className="font-sans text-sm">
              {t("founding.assign.result", {
                succeeded: result.succeeded.length,
                failed: result.failed.length,
              })}
            </p>
            {result.failed.length > 0 && (
              <ul className="max-h-40 space-y-1 overflow-y-auto font-sans text-xs text-muted-foreground">
                {result.failed.map((f) => (
                  <li key={f.propertyId}>
                    {f.propertyId}: {f.errorCode}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="founding-program">{t("founding.assign.program")}</Label>
              <select
                id="founding-program"
                data-testid="assign-founding-program"
                value={program}
                onChange={(e) => setProgram(e.target.value as FoundingProgram)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 font-sans text-sm"
              >
                {FOUNDING_PROGRAMS.map((p) => (
                  <option key={p} value={p}>
                    {t(`founding.program.${p}`)}
                  </option>
                ))}
              </select>
            </div>

            {isCustom && (
              <div className="space-y-1.5">
                <Label>{t("founding.assign.structure")}</Label>
                <div
                  className="grid grid-cols-2 gap-2"
                  role="radiogroup"
                  aria-label={t("founding.assign.structure")}
                >
                  <Button
                    type="button"
                    size="sm"
                    variant={structure === "two-phase" ? "default" : "outline"}
                    role="radio"
                    aria-checked={structure === "two-phase"}
                    data-testid="assign-founding-structure-two-phase"
                    onClick={() => setStructure("two-phase")}
                  >
                    {t("founding.assign.structureTwoPhase")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={structure === "flat" ? "default" : "outline"}
                    role="radio"
                    aria-checked={structure === "flat"}
                    data-testid="assign-founding-structure-flat"
                    onClick={() => setStructure("flat")}
                  >
                    {t("founding.assign.structureFlat")}
                  </Button>
                </div>
                <p className="font-sans text-xs text-muted-foreground">
                  {t("founding.assign.structureHelper")}
                </p>
              </div>
            )}

            {showTwoPhase ? (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="founding-intro">{t("founding.assign.introPercent")}</Label>
                  <Input
                    id="founding-intro"
                    data-testid="assign-founding-intro"
                    type="number"
                    min={MIN_DISCOUNT_PERCENT}
                    max={MAX_DISCOUNT_PERCENT}
                    placeholder={String(FOUNDING_INTRO_PLACEHOLDER)}
                    value={intro}
                    onChange={(e) => setIntro(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="founding-ongoing">{t("founding.assign.ongoingPercent")}</Label>
                  <Input
                    id="founding-ongoing"
                    data-testid="assign-founding-ongoing"
                    type="number"
                    min={MIN_DISCOUNT_PERCENT}
                    max={MAX_DISCOUNT_PERCENT}
                    placeholder={String(FOUNDING_ONGOING_PLACEHOLDER)}
                    value={ongoing}
                    onChange={(e) => setOngoing(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="founding-flat">{t("founding.assign.flatPercent")}</Label>
                <Input
                  id="founding-flat"
                  data-testid="assign-founding-flat"
                  type="number"
                  min={MIN_DISCOUNT_PERCENT}
                  max={MAX_DISCOUNT_PERCENT}
                  value={flat}
                  onChange={(e) => setFlat(e.target.value)}
                />
              </div>
            )}

            {error && (
              <p data-testid="assign-founding-error" className="font-sans text-sm text-destructive">
                {error}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button
              type="button"
              data-testid="assign-founding-close"
              onClick={() => onOpenChange(false)}
            >
              {t("founding.assign.close")}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={assign.isPending}
              >
                {t("founding.assign.cancel")}
              </Button>
              <Button
                type="button"
                data-testid="assign-founding-submit"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {t("founding.assign.confirm")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
